/**
 * @fileoverview Branch Analytics — Spread, C/I Ratio & Income Mix Analysis page.
 * @description Ported from the InsightBox Banking design system's own
 * `BranchAnalytics/SpreadCiIncomeMix.tsx`.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useCurrentSession } from '@/features/auth'
import {
  BranchFilterSelect,
  FIVE_YEAR_ID,
  FyFilterSelect,
  LastUpdatedLabel,
  branchLabel,
  formatCr,
  incomeExpenseMix,
  resolveBankingAccessScope,
  resolveFy,
  useBankingData,
} from '@/features/banking'
import BankingKpiStrip from '@/shared/components/molecules/BankingKpiStrip'
import BusinessSummaryPanel from '@/shared/components/molecules/BusinessSummaryPanel'
import ChartCard from '@/shared/components/molecules/ChartCard'
import KpiCard from '@/shared/components/molecules/KpiCard'
import PageHeader from '@/shared/components/molecules/PageHeader'
import { PieBreakdownCardGroup } from '@/shared/components/molecules/PieBreakdownCard'
import { categoricalPalette } from '@/shared/constants/chart-theme.constants'

import type { FyFilter } from '@/features/banking'
import type { BankingKpiStripItem } from '@/shared/components/molecules/BankingKpiStrip'
import type { PieGroup } from '@/shared/components/molecules/PieBreakdownCard'

const [MIX_COLOR_INTEREST, MIX_COLOR_NON_INTEREST] = categoricalPalette(2)

/**
 * @component
 */
function BankingBranchAnalyticsSpreadCiIncomeMixPage() {
  const { t } = useTranslation()
  const dataQuery = useBankingData()
  const [fyFilter, setFyFilter] = useState<FyFilter>(FIVE_YEAR_ID)
  const bankingScope = resolveBankingAccessScope(useCurrentSession().data?.bankingScope)
  const [branchId, setBranchId] = useState(bankingScope.defaultBranchId)

  const header = (
    <PageHeader
      kicker={t('banking.branchAnalyticsSpreadCiIncomeMix.kicker')}
      title={t('banking.branchAnalyticsSpreadCiIncomeMix.title')}
      description={t('banking.branchAnalyticsSpreadCiIncomeMix.description', {
        fy: resolveFy(fyFilter),
      })}
      filters={
        <>
          <FyFilterSelect value={fyFilter} onChange={setFyFilter} />
          <BranchFilterSelect value={branchId} onChange={setBranchId} scope={bankingScope} />
          {dataQuery.data && <LastUpdatedLabel value={dataQuery.data.meta.lastUpdated} />}
        </>
      }
    />
  )

  if (dataQuery.isPending) {
    return (
      <div className="flex flex-col gap-4">
        {header}
        <BankingKpiStrip
          loading
          items={[
            { label: '', value: '', highlight: true },
            { label: '', value: '' },
            { label: '', value: '' },
            { label: '', value: '' },
          ]}
        />
        <ChartCard title="" loading>
          <div />
        </ChartCard>
      </div>
    )
  }

  if (dataQuery.isError) {
    return (
      <div className="flex flex-col gap-4">
        {header}
        <p className="text-sm text-danger">{t('banking.loadError')}</p>
      </div>
    )
  }

  const data = dataQuery.data
  const fy = resolveFy(fyFilter)
  const m = incomeExpenseMix(data, fy, branchId)

  const incomeExpenseGroups: PieGroup[] = [
    {
      key: 'income',
      label: t('banking.branchAnalyticsSpreadCiIncomeMix.groups.income.label'),
      sublabel: t('banking.branchAnalyticsSpreadCiIncomeMix.groups.total', {
        value: formatCr(m.income),
      }),
      categoryColumnLabel: t(
        'banking.branchAnalyticsSpreadCiIncomeMix.groups.income.categoryColumn',
      ),
      valueColumnLabel: t('banking.amountCrColumn'),
      segments: [
        {
          key: 'interest',
          label: t('banking.branchAnalyticsSpreadCiIncomeMix.groups.interestIncome'),
          value: m.interestIncome,
          pct: (m.interestIncome / m.income) * 100,
          color: MIX_COLOR_INTEREST!,
        },
        {
          key: 'non-interest',
          label: t('banking.branchAnalyticsSpreadCiIncomeMix.groups.nonInterestIncome'),
          value: m.nonInterestIncome,
          pct: (m.nonInterestIncome / m.income) * 100,
          color: MIX_COLOR_NON_INTEREST!,
        },
      ],
    },
    {
      key: 'expenses',
      label: t('banking.branchAnalyticsSpreadCiIncomeMix.groups.expenses.label'),
      sublabel: t('banking.branchAnalyticsSpreadCiIncomeMix.groups.total', {
        value: formatCr(m.expenses),
      }),
      categoryColumnLabel: t(
        'banking.branchAnalyticsSpreadCiIncomeMix.groups.expenses.categoryColumn',
      ),
      valueColumnLabel: t('banking.amountCrColumn'),
      segments: [
        {
          key: 'interest',
          label: t('banking.branchAnalyticsSpreadCiIncomeMix.groups.interestExpense'),
          value: m.interestExpense,
          pct: (m.interestExpense / m.expenses) * 100,
          color: MIX_COLOR_INTEREST!,
        },
        {
          key: 'non-interest',
          label: t('banking.branchAnalyticsSpreadCiIncomeMix.groups.nonInterestExpense'),
          value: m.nonInterestExpense,
          pct: (m.nonInterestExpense / m.expenses) * 100,
          color: MIX_COLOR_NON_INTEREST!,
        },
      ],
    },
  ]

  const kpis: BankingKpiStripItem[] = [
    {
      label: t('banking.branchAnalyticsSpreadCiIncomeMix.kpi.spread'),
      value: `${m.spreadPct.toFixed(2)}%`,
      sublabel: t('banking.branchAnalyticsSpreadCiIncomeMix.kpi.interestMargin'),
      highlight: true,
    },
    {
      label: t('banking.branchAnalyticsSpreadCiIncomeMix.kpi.ciRatio'),
      value: `${m.ciRatio.toFixed(2)}%`,
      sublabel: t('banking.branchAnalyticsSpreadCiIncomeMix.kpi.ciRatioSublabel'),
    },
    {
      label: t('banking.branchAnalyticsSpreadCiIncomeMix.kpi.totalIncome'),
      value: formatCr(m.income),
      sublabel: t('banking.branchAnalyticsSpreadCiIncomeMix.kpi.interestPlusNonInterest'),
    },
    {
      label: t('banking.branchAnalyticsSpreadCiIncomeMix.kpi.totalExpenses'),
      value: formatCr(m.expenses),
      sublabel: t('banking.branchAnalyticsSpreadCiIncomeMix.kpi.interestPlusNonInterest'),
    },
  ]

  return (
    <div className="flex flex-col gap-4">
      {header}
      <BankingKpiStrip items={kpis} />

      <ChartCard
        title={t('banking.branchAnalyticsSpreadCiIncomeMix.chartTitle', { fy })}
        subtitle={t('banking.branchAnalyticsSpreadCiIncomeMix.chartSubtitle', {
          branch: branchLabel(branchId),
        })}
      >
        <PieBreakdownCardGroup
          groups={incomeExpenseGroups}
          valueFormatter={(v) => formatCr(v)}
          totalLabel={t('banking.total')}
        />
      </ChartCard>

      <div>
        <h2 className="mb-2 text-sm font-bold text-text">
          {t('banking.branchAnalyticsSpreadCiIncomeMix.efficiencySnapshotTitle', { fy })}
        </h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <KpiCard
            highlight
            label={t('banking.branchAnalyticsSpreadCiIncomeMix.kpi.totalIncome')}
            value={formatCr(m.income)}
            sublabel={t('banking.branchAnalyticsSpreadCiIncomeMix.kpi.interestPlusNonInterest')}
          />
          <KpiCard
            tone="red"
            label={t('banking.branchAnalyticsSpreadCiIncomeMix.kpi.totalExpenses')}
            value={formatCr(m.expenses)}
            sublabel={t('banking.branchAnalyticsSpreadCiIncomeMix.kpi.expenseAbsorption', {
              pct: ((m.expenses / m.income) * 100).toFixed(2),
            })}
          />
          <KpiCard
            tone="green"
            label={t('banking.branchAnalyticsSpreadCiIncomeMix.kpi.residual')}
            value={formatCr(m.residual)}
            sublabel={t('banking.branchAnalyticsSpreadCiIncomeMix.kpi.residualSublabel', {
              pct: m.residualToIncomePct.toFixed(2),
            })}
          />
        </div>
      </div>

      <BusinessSummaryPanel
        layout="horizontal"
        title={t('banking.businessSummary')}
        lines={[
          t('banking.branchAnalyticsSpreadCiIncomeMix.summary.line1', {
            interestPct: ((m.interestIncome / m.income) * 100).toFixed(2),
            nonInterestPct: ((m.nonInterestIncome / m.income) * 100).toFixed(2),
          }),
          t('banking.branchAnalyticsSpreadCiIncomeMix.summary.line2', {
            ciRatio: m.ciRatio.toFixed(2),
            residual: formatCr(m.residual),
            residualPct: m.residualToIncomePct.toFixed(2),
          }),
        ]}
      />
    </div>
  )
}

export default BankingBranchAnalyticsSpreadCiIncomeMixPage
